"use client"

import { dateLib, moneyLib } from "@agentcall/core/lib"
import { tenantType } from "@agentcall/core/type"
import { zodResolver } from "@hookform/resolvers/zod"
import { useEffect, useMemo, useRef } from "react"
import { type Resolver, useFieldArray, useForm } from "react-hook-form"
import * as z from "zod"
import { EchoesEditGuard } from "@/components/echoes/EchoesEditGuard"
import { Button } from "@/components/ui/button/button"
import { Card, CardContent } from "@/components/ui/card/card"
import { Checkbox } from "@/components/ui/checkbox/checkbox"
import { DatePicker } from "@/components/ui/date-picker/date-picker"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormRequiredLabel,
} from "@/components/ui/form/form"
import { Input } from "@/components/ui/input/input"
import { PriceWithTax } from "@/components/ui/price-with-tax"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select/select"
import { OccupantsFields, OtherFeesFields } from "@/pages/new-tenant-register/components"

const normalizeDateValue = (value: Date | string | null | undefined) => {
  if (!value) {
    return undefined
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? undefined : value
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [year, month, day] = value.split("-").map(Number)
    return new Date(year, month - 1, day, 12)
  }

  const parsed = new Date(value)
  return Number.isNaN(parsed.getTime()) ? undefined : parsed
}

const getDigits = (value: string, maxLength: number) =>
  value.replace(/[^0-9]/g, "").slice(0, maxLength)

export const tenantFormSchema = z
  .object({
    fullName: tenantType.nameZ,
    fullNameKana: tenantType.nameKanaZ,
    phone1: tenantType.phone1Z,
    phone2: tenantType.phone2Z,
    phone3: tenantType.phone3Z,
    phone4: tenantType.phone4Z.default(""),
    phone5: tenantType.phone5Z.default(""),
    phone6: tenantType.phone6Z.default(""),
    email: z.string().email("有効なメールアドレスを入力してください").or(z.literal("")),
    purpose: tenantType.purposeZ,
    startDate: tenantType.startDateZ,
    endDate: z.date().optional(),
    rent: z.string().optional(),
    maintenanceFee: z.string().optional(),
    hasParking: z.boolean().default(false),
    parkingLotNumber: z.string().optional(),
    parkingFee: z.string().optional(),
    otherFees: z
      .array(
        z.object({
          name: z.string(),
          baseFee: z.string(),
          tax: z.string(),
        })
      )
      .default([]),
    occupants: z
      .array(
        z.object({
          name: z.string().min(1, "入居者の氏名を入力してください"),
          nameKana: z.string(),
          phone: z.string(),
          birthday: z.string(),
          note: z.string(),
        })
      )
      .default([]),
  })
  .refine(
    (data) => {
      const values = [data.phone4, data.phone5, data.phone6]
      return values.every((value) => !value) || values.every((value) => !!value)
    },
    {
      message: "電話番号②を入力する場合はすべて入力してください",
      path: ["phone4"],
    }
  )
  .refine(
    (data) => {
      if (data.endDate) {
        return data.endDate >= new Date(data.startDate)
      }
      return true
    },
    {
      message: "契約終了日は契約開始日以降を選択してください",
      path: ["endDate"],
    }
  )

type TenantFormFormValues = z.input<typeof tenantFormSchema>
export type TenantFormValues = z.output<typeof tenantFormSchema>

const tenantFormResolver: Resolver<TenantFormFormValues, unknown, TenantFormValues> = (
  values,
  context,
  options
) => zodResolver(tenantFormSchema)(values, context, options)

export type TenantFormDefaultValues = Partial<
  Omit<TenantFormFormValues, "startDate" | "endDate">
> & {
  startDate?: TenantFormFormValues["startDate"] | Date | null
  endDate?: TenantFormFormValues["endDate"] | string | Date | null
}

interface TenantFormProps {
  defaultValues?: TenantFormDefaultValues
  onSubmit: (values: TenantFormValues) => Promise<void>
  onCancel: () => void
  onDelete?: () => void
  isEdit?: boolean
  isSubmitting: boolean
}

export function TenantForm({
  defaultValues,
  onSubmit,
  onCancel,
  onDelete,
  isEdit = false,
  isSubmitting,
}: TenantFormProps) {
  const hasResetRef = useRef(false)

  const normalizedDefaults = useMemo<Partial<TenantFormFormValues>>(() => {
    const base: Partial<TenantFormFormValues> = {
      fullName: defaultValues?.fullName ?? "",
      fullNameKana: defaultValues?.fullNameKana ?? "",
      phone1: defaultValues?.phone1 ?? "",
      phone2: defaultValues?.phone2 ?? "",
      phone3: defaultValues?.phone3 ?? "",
      phone4: defaultValues?.phone4 ?? "",
      phone5: defaultValues?.phone5 ?? "",
      phone6: defaultValues?.phone6 ?? "",
      email: defaultValues?.email ?? "",
      purpose: defaultValues?.purpose,
      rent: defaultValues?.rent ?? "",
      maintenanceFee: defaultValues?.maintenanceFee ?? "",
      hasParking: defaultValues?.hasParking ?? false,
      parkingLotNumber: defaultValues?.parkingLotNumber ?? "",
      parkingFee: defaultValues?.parkingFee ?? "",
      otherFees:
        defaultValues?.otherFees && defaultValues.otherFees.length > 0
          ? defaultValues.otherFees
          : [{ name: "", baseFee: "", tax: "" }],
      occupants: defaultValues?.occupants ?? [],
    }

    const startDateValue = normalizeDateValue(defaultValues?.startDate)
    if (startDateValue) {
      base.startDate = dateLib.formatDateISO(startDateValue)
    }

    const endDateValue = normalizeDateValue(defaultValues?.endDate)
    if (endDateValue) {
      base.endDate = endDateValue
    }

    return base
  }, [defaultValues])

  const form = useForm<TenantFormFormValues>({
    resolver: tenantFormResolver,
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      hasParking: false,
      ...normalizedDefaults,
    },
  })

  useEffect(() => {
    if (isEdit && defaultValues && !hasResetRef.current) {
      form.reset({
        hasParking: false,
        ...normalizedDefaults,
      })
      hasResetRef.current = true
      // reset後にバリデーションをトリガーしてエラーを即表示する
      void form.trigger()
    }
  }, [normalizedDefaults, isEdit, defaultValues, form.reset, form.trigger])

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "otherFees",
  })

  const {
    fields: occupantFields,
    append: appendOccupant,
    remove: removeOccupant,
  } = useFieldArray({
    control: form.control,
    name: "occupants",
  })

  const watchPurpose = form.watch("purpose")
  const watchRent = form.watch("rent")
  const watchMaintenanceFee = form.watch("maintenanceFee")
  const watchHasParking = form.watch("hasParking")
  const watchParkingFee = form.watch("parkingFee")

  const isBusiness = watchPurpose === tenantType.TENANT_PURPOSE_BUSINESS

  const rentDetails = useMemo(() => {
    const baseFee = Number.parseFloat(watchRent || "0")
    const tax = moneyLib.calculateTax(baseFee)
    const total = baseFee + tax
    return { baseFee, tax, total }
  }, [watchRent])

  const maintenanceFeeDetails = useMemo(() => {
    const baseFee = Number.parseFloat(watchMaintenanceFee || "0")
    const tax = moneyLib.calculateTax(baseFee)
    const total = baseFee + tax
    return { baseFee, tax, total }
  }, [watchMaintenanceFee])

  const parkingDetails = useMemo(() => {
    const baseFee = Number.parseFloat(watchParkingFee || "0")
    const tax = moneyLib.calculateTax(baseFee)
    const total = baseFee + tax
    return { baseFee, tax, total }
  }, [watchParkingFee])

  const { isDirty, isValid } = form.formState

  const primaryButtonLabel = isEdit ? "更新する" : "登録する"
  const handleSubmitForm = async (values: TenantFormFormValues) => {
    const parsedValues = tenantFormSchema.parse(values)
    await onSubmit(parsedValues)
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmitForm)} className="space-y-4">
        <Card className="border-slate-200">
          <CardContent className="space-y-4 p-4">
            <div className="bg-slate-100 px-3.5 py-3">
              <h2 className="text-sm font-bold text-primary">基本情報</h2>
            </div>
            <FormField
              control={form.control}
              name="fullName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center gap-1 text-sm font-semibold text-slate-600">
                    氏名
                    <FormRequiredLabel />
                  </FormLabel>
                  <FormControl>
                    <Input placeholder="田中 太郎" className="h-[42px]" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="fullNameKana"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center gap-1 text-sm font-semibold text-slate-600">
                    氏名（カナ）
                    <FormRequiredLabel />
                  </FormLabel>
                  <FormControl>
                    <Input placeholder="タナカ タロウ" className="h-[42px]" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormLabel className="flex items-center gap-1 text-sm font-semibold text-slate-600 mb-2">
              電話番号①
              <FormRequiredLabel />
            </FormLabel>
            <FormLabel className="hidden">
              電話番号
              <FormRequiredLabel />
            </FormLabel>
            <div className="grid grid-cols-3 gap-x-4">
              <FormField
                control={form.control}
                name="phone1"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        type="tel"
                        placeholder="090"
                        className="h-[42px]"
                        inputMode="numeric"
                        {...field}
                        maxLength={4}
                        onChange={(e) => field.onChange(getDigits(e.target.value, 4))}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="phone2"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        type="tel"
                        placeholder="1234"
                        className="h-[42px]"
                        inputMode="numeric"
                        {...field}
                        maxLength={4}
                        onChange={(e) => field.onChange(getDigits(e.target.value, 4))}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="phone3"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        type="tel"
                        placeholder="5678"
                        className="h-[42px]"
                        inputMode="numeric"
                        {...field}
                        maxLength={4}
                        onChange={(e) => field.onChange(getDigits(e.target.value, 4))}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormLabel className="flex items-center gap-1 text-sm font-semibold text-slate-600 mb-2">
              電話番号②
            </FormLabel>
            <div className="grid grid-cols-3 gap-x-4">
              <FormField
                control={form.control}
                name="phone4"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        type="tel"
                        placeholder="03"
                        className="h-[42px]"
                        inputMode="numeric"
                        {...field}
                        maxLength={4}
                        onChange={(e) => field.onChange(getDigits(e.target.value, 4))}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="phone5"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        type="tel"
                        placeholder="1234"
                        className="h-[42px]"
                        inputMode="numeric"
                        {...field}
                        maxLength={4}
                        onChange={(e) => field.onChange(getDigits(e.target.value, 4))}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="phone6"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        type="tel"
                        placeholder="5678"
                        className="h-[42px]"
                        inputMode="numeric"
                        {...field}
                        maxLength={4}
                        onChange={(e) => field.onChange(getDigits(e.target.value, 4))}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center gap-1 text-sm font-semibold text-slate-600">
                    メールアドレス
                  </FormLabel>
                  <FormControl>
                    <Input
                      placeholder="tanaka@example.com"
                      className="h-[42px]"
                      type="email"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="purpose"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center gap-1 text-sm font-semibold text-slate-600">
                    用途
                    <FormRequiredLabel />
                  </FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger className="h-[42px]">
                        <SelectValue placeholder="用途を選択" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {tenantType.TENANT_PURPOSES.map((purpose) => (
                        <SelectItem key={purpose.key} value={purpose.key}>
                          {purpose.value}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="startDate"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center gap-1 text-sm font-semibold text-slate-600">
                    初回賃貸借契約開始日
                    <FormRequiredLabel />
                  </FormLabel>
                  <FormControl>
                    <DatePicker
                      id={field.name}
                      value={normalizeDateValue(field.value)}
                      onChange={(date) => field.onChange(date ? dateLib.formatDateISO(date) : "")}
                      placeholder="日付を選択"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* <FormField
              control={form.control}
              name="endDate"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-semibold text-slate-600">契約終了日</FormLabel>
                  <DatePicker
                    id={field.name}
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="----年--月--日"
                    className="h-[42px] border-slate-300"
                  />
                  <FormMessage />
                </FormItem>
              )}
            /> */}
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardContent className="space-y-4 p-4">
            <div className="bg-slate-100 px-3.5 py-3">
              <h2 className="text-sm font-bold text-primary">料金情報</h2>
            </div>

            <div className="space-y-1.5">
              <FormField
                control={form.control}
                name="rent"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="font-semibold text-sm text-slate-600">家賃</FormLabel>
                    <FormControl>
                      <div className="flex items-end gap-1.5">
                        <Input placeholder="85000" type="number" {...field} className="flex-1" />
                        <span className="text-sm font-semibold text-slate-600 pb-2">円</span>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {isBusiness && (
                <PriceWithTax
                  baseFee={String(rentDetails.baseFee)}
                  tax={String(rentDetails.tax)}
                  total={rentDetails.total}
                />
              )}
            </div>

            <div className="space-y-1.5">
              <FormField
                control={form.control}
                name="maintenanceFee"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="font-semibold text-sm text-slate-600">
                      共益費・管理費
                    </FormLabel>
                    <FormControl>
                      <div className="flex items-end gap-1.5">
                        <Input placeholder="5000" type="number" {...field} className="flex-1" />
                        <span className="text-sm font-bold text-slate-600 pb-2">円</span>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {isBusiness && (
                <PriceWithTax
                  baseFee={String(maintenanceFeeDetails.baseFee)}
                  tax={String(maintenanceFeeDetails.tax)}
                  total={maintenanceFeeDetails.total}
                />
              )}
            </div>

            <FormField
              control={form.control}
              name="hasParking"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="font-semibold text-sm text-slate-600">駐車場</FormLabel>
                  <div className="flex items-center space-x-1.5 py-1.5">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        id="hasParking"
                      />
                    </FormControl>
                    <label
                      htmlFor="hasParking"
                      className="text-sm font-medium text-slate-600 leading-none cursor-pointer"
                    >
                      駐車場あり
                    </label>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />

            {watchHasParking && (
              <>
                <FormField
                  control={form.control}
                  name="parkingLotNumber"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="font-semibold text-sm text-slate-600">
                        区画番号
                      </FormLabel>
                      <FormControl>
                        <Input placeholder="1" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="space-y-1.5">
                  <FormField
                    control={form.control}
                    name="parkingFee"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="font-semibold text-sm text-slate-600">
                          駐車場代
                        </FormLabel>
                        <FormControl>
                          <div className="flex items-end gap-1.5">
                            <Input placeholder="8000" type="number" {...field} className="flex-1" />
                            <span className="text-sm font-bold text-slate-600 pb-2">円</span>
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <PriceWithTax
                    baseFee={String(parkingDetails.baseFee)}
                    tax={String(parkingDetails.tax)}
                    total={parkingDetails.total}
                  />
                </div>
              </>
            )}

            <OtherFeesFields form={form} fields={fields} append={append} remove={remove} />
          </CardContent>
        </Card>

        <Card>
          <CardContent className="space-y-4 p-4">
            <OccupantsFields
              form={form}
              fields={occupantFields}
              append={appendOccupant}
              remove={removeOccupant}
            />
          </CardContent>
        </Card>

        <div className="flex items-center justify-end gap-3">
          {isEdit && onDelete && (
            <div className="flex-1">
              <EchoesEditGuard>
                <Button
                  type="button"
                  variant="destructive"
                  className="h-[42px] px-6"
                  onClick={onDelete}
                  disabled={isSubmitting}
                >
                  削除する
                </Button>
              </EchoesEditGuard>
            </div>
          )}
          <Button
            type="button"
            variant="outline"
            className="h-[42px] font-semibold text-slate-600"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            キャンセル
          </Button>
          <EchoesEditGuard>
            <Button
              type="submit"
              className="h-[42px] font-semibold"
              disabled={(!isEdit && !isDirty) || !isValid}
              loading={isSubmitting}
            >
              {primaryButtonLabel}
            </Button>
          </EchoesEditGuard>
        </div>
      </form>
    </Form>
  )
}
